<template>
  <div class="w-full pb-4 pt-2">
    <div
      class="group relative flex min-h-60 w-full items-center justify-center rounded border p-2"
    >
      <!-- Edit button overlay with dimmer (only show for user templates) -->
      <div
        v-if="!props.isDefaultTemplate"
        class="pointer-events-none absolute inset-0 z-30 flex cursor-pointer items-center justify-center bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-40"
      >
        <UIButton
          id="edit-whatsapp-template"
          size="small"
          class="pointer-events-auto z-40 border border-gray-300 bg-white text-black opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100"
          @click="props.openEditModal"
        >
          <Pencil01Icon class="h-6 w-6 text-black" />
          <span class="ml-2 font-medium text-black">{{
            t('labels.edit')
          }}</span>
        </UIButton>
      </div>

      <!-- Disabled overlay for default templates (no edit button, just dimmer) -->
      <div
        v-else
        class="pointer-events-none absolute inset-0 z-30 cursor-not-allowed bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-20"
      ></div>

      <!-- WhatsApp Preview Container -->
      <div
        class="whatsapp-preview-container relative h-full min-h-[240px] w-full overflow-hidden rounded-lg"
      >
        <!-- Background Pattern -->
        <div class="whatsapp-background absolute inset-0"></div>

        <!-- WhatsApp Message Bubble -->
        <div
          class="relative z-10 flex h-full max-h-80 flex-col justify-end overflow-hidden p-3"
        >
          <div
            class="mb-2 flex max-h-[calc(100%-30px)] min-h-0 w-full flex-col self-end overflow-hidden rounded-lg bg-white shadow-sm"
            :class="messageBubbleClass"
          >
            <!-- Scrollable content: media / LTO / body / footer / carousel -->
            <div class="min-h-0 flex-1 overflow-y-auto p-3 pb-1">
              <!-- Header -->
              <div
                v-if="model.header.show"
                class="mb-1 break-words text-[11px] font-bold text-gray-900"
              >
                <span v-if="model.header.type === 'TEXT'">{{
                  model.header.text
                }}</span>
                <img
                  v-else-if="
                    model.header.type === 'IMAGE' && model.header.mediaUrl
                  "
                  :src="model.header.mediaUrl"
                  class="mb-1 h-[112px] max-h-[112px] w-full rounded-md object-cover"
                  alt=""
                />
                <video
                  v-else-if="
                    model.header.type === 'VIDEO' && model.header.mediaUrl
                  "
                  :key="model.header.mediaUrl"
                  controls
                  class="mb-1 h-[112px] max-h-[112px] w-full rounded-md object-cover"
                >
                  <source :src="model.header.mediaUrl" />
                  <track kind="captions" />
                </video>
                <div
                  v-else-if="model.header.type === 'DOCUMENT'"
                  class="mb-1 rounded-md bg-gray-100 px-2 py-1.5 text-[10px] font-medium text-gray-700"
                >
                  {{ t('eventNotification.whatsapp.documentPreview') }}
                </div>
              </div>

              <!-- Limited Time Offer -->
              <div
                v-if="showLimitedTimeOffer"
                id="limited-time-offer-preview"
                class="mb-1 flex items-start gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2"
              >
                <span
                  class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-600"
                >
                  <svg
                    class="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8M12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7zM2 7h20v5H2V7z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <div class="flex min-w-0 flex-col text-[11px]">
                  <span
                    v-if="model.limitedTimeOffer?.heading"
                    id="limited-time-offer-preview-heading"
                    class="font-medium text-gray-900"
                  >
                    {{ model.limitedTimeOffer.heading }}
                  </span>
                  <span
                    v-if="limitedTimeOfferEndsOnText"
                    id="limited-time-offer-preview-expiration"
                    class="text-[10px] leading-tight text-gray-600"
                  >
                    {{ limitedTimeOfferEndsOnText }}
                  </span>
                  <span
                    v-if="model.limitedTimeOffer?.offerCode"
                    id="limited-time-offer-preview-code"
                    class="text-[10px] leading-tight text-gray-600"
                  >
                    {{
                      t('eventNotification.whatsapp.offerCode', {
                        code: model.limitedTimeOffer.offerCode,
                      })
                    }}
                  </span>
                </div>
              </div>

              <!-- Body Text -->
              <div
                v-if="model.body"
                class="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-gray-800"
              >
                {{ model.body }}
              </div>

              <!-- Footer -->
              <div
                v-if="model.footer"
                class="mt-1 break-words text-[9px] text-gray-500"
              >
                {{ model.footer }}
              </div>

              <!-- Carousel cards -->
              <div
                v-if="model.carouselCards.length"
                id="message-preview-carousel-cards"
                class="carousel-cards-scroll -mx-1 mt-1 flex gap-1 overflow-x-auto px-1 pb-1"
              >
                <div
                  v-for="card in model.carouselCards"
                  :key="card.id"
                  class="flex max-h-[260px] w-[220px] min-w-[220px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-[#dadde1] bg-white"
                >
                  <img
                    v-if="card.mediaType === 'IMAGE' && card.mediaUrl"
                    :src="card.mediaUrl"
                    class="h-[128px] max-h-[128px] w-full flex-shrink-0 object-cover"
                    alt=""
                  />
                  <video
                    v-else-if="card.mediaType === 'VIDEO' && card.mediaUrl"
                    :key="card.mediaUrl"
                    controls
                    class="h-[128px] max-h-[128px] w-full flex-shrink-0 object-cover"
                  >
                    <source :src="card.mediaUrl" />
                    <track kind="captions" />
                  </video>
                  <div
                    v-else
                    class="flex h-[128px] max-h-[128px] w-full flex-shrink-0 items-center justify-center bg-gray-300 text-white"
                  >
                    <svg
                      class="h-8 w-8"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <rect
                        x="3"
                        y="5"
                        width="18"
                        height="14"
                        rx="2"
                        stroke="currentColor"
                        stroke-width="1.5"
                      />
                      <circle cx="8.5" cy="10" r="1.5" fill="currentColor" />
                      <path
                        d="M3 16l5-4 4 3 4-5 5 6"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <div
                    v-if="card.body"
                    class="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words p-2 text-[10px] font-normal text-gray-700"
                  >
                    {{ card.body }}
                  </div>
                  <div class="flex-shrink-0">
                    <div
                      v-for="button in card.buttons"
                      :key="button.id"
                      class="border-t border-[#dadde1] px-2 py-2 text-center text-[10px] text-success-600"
                    >
                      {{ button.text }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Default message when no template content -->
              <div v-if="isEmptyPreview" class="text-[11px] text-gray-500">
                {{ t('eventNotification.whatsapp.noPreview') }}
              </div>
            </div>

            <!-- Sticky top-level buttons (non-carousel) — always visible -->
            <div
              v-if="!model.carouselCards.length && model.buttons.length"
              class="flex-shrink-0 px-1"
            >
              <div
                v-for="button in model.buttons.slice(0, maxButtons)"
                :key="button.id"
                class="border-t border-[#dadde1] py-1.5 text-center text-[10px] text-success-600"
              >
                {{ button.text }}
              </div>
            </div>
          </div>

          <!-- Timestamp -->
          <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
          <div class="mr-1 flex-shrink-0 self-end text-[9px] text-gray-600">
            11:48 am
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Pencil01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UIButton } from '@gohighlevel/ghl-ui'
import {
  buildSendTemplatePreviewModel,
  formatOfferExpirationDate,
  type WhatsAppTemplateLike,
} from '@/utils/whatsappTemplatePreview'
import { computed, PropType } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const maxButtons = 3

const props = defineProps({
  templateId: {
    type: String,
    required: true,
  },
  template: {
    type: Object as PropType<WhatsAppTemplateLike | null>,
    default: null,
  },
  openEditModal: {
    type: Function,
    required: true,
  },
  isDefaultTemplate: {
    type: Boolean,
    default: false,
  },
})

const model = computed(() => buildSendTemplatePreviewModel(props.template))

const isMediaHeader = computed(() =>
  ['IMAGE', 'VIDEO'].includes(model.value.header.type)
)

/** Carousel cards stay 220px (same as regular media); bubble is slightly wider so the next card peeks. */
const messageBubbleClass = computed(() => {
  if (model.value.carouselCards.length) {
    return 'w-[248px] max-w-[248px]'
  }
  if (isMediaHeader.value) {
    return 'w-[220px] max-w-[220px]'
  }
  if (model.value.limitedTimeOffer !== null) {
    return 'w-[240px] max-w-[240px]'
  }
  return 'max-w-[85%]'
})

const showLimitedTimeOffer = computed(() => model.value.limitedTimeOffer !== null)

const limitedTimeOfferEndsOnText = computed(() => {
  const expirationDate = model.value.limitedTimeOffer?.expirationDate
  if (!expirationDate) return null
  return t('eventNotification.whatsapp.offerEndsOn', {
    date: formatOfferExpirationDate(expirationDate),
  })
})

const isEmptyPreview = computed(() => {
  const preview = model.value
  return (
    !preview.header.show &&
    !preview.body &&
    !preview.footer &&
    !preview.limitedTimeOffer &&
    !preview.carouselCards.length &&
    !preview.buttons.length
  )
})
</script>

<style lang="scss" scoped>
.whatsapp-preview-container {
  min-height: 240px;
  background-color: #e5ddd5; /* WhatsApp default background color */
}

.whatsapp-background {
  background-image: url('/src/assets/whatsapp-background-pattern.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.4;
}

.carousel-cards-scroll {
  scrollbar-width: thin;
}

/* Custom scrollbar for potential overflow */
.whatsapp-preview-container::-webkit-scrollbar {
  width: 2px;
}

.whatsapp-preview-container::-webkit-scrollbar-track {
  background: transparent;
}

.whatsapp-preview-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 1px;
}
</style>
